// File: noviopus-ats-client/src/features/Job/sagas.ts
import { takeLatest, takeLeading, call, put, select } from 'redux-saga/effects'; // Import takeLeading
import { toastr } from 'react-redux-toastr';
import { PayloadAction } from '@reduxjs/toolkit';
import { push } from '@/App/router/actions';
import { trackNoviopusEvent } from '@/utils/analytics';

import { 
    submitJob, 
    submitJobSuccess, 
    submitJobFailure,
    deleteJobDraft,
    deleteJobDraftSuccess,
    startParsing,
    parsingSuccess,
    parsingFailure,
    startPreFlightJDParsing,
    loadJobForEdit,
    initiateCreateJob, 
    openOrgSelector, 
    setJobCreationContext,
    resetJobDraft,
    fetchJobDetails,
    updateDraft
} from './jobCreateSlice';
import * as api from './api';
import { selectJobDraft } from './selectors';
import { selectCurrentAccount } from '@/features/Auth/selectors';
import { JobDraft, DEFAULT_STAGES } from './types';
import { selectAccountData } from '@/features/Account/selectors'; 
import { getJobStepPath } from '@/Pages/Recruitment/Job/jobTracks';
import { selectActiveOrganizationDetails } from '@/features/Navigation/selectors';
import { setActiveOrganization } from '@/features/Navigation/contextSlice';
import { fetchOrganization } from '@/features/Organization/organizationSlice';
import i18n from '@/i18n';

// --- TRANSACTION LOCK ---
// Prevents duplicate creation across rapid wizard navigations (Redux state lag)
let sessionCreatedJobId: string | null = null;

const flattenDraftForApi = (draft: any) => {
    // FIX: Prioritize MongoDB _id, then id, then value, then raw string
    const extractId = (val: any) => {
        if (val && typeof val === 'object') return val._id || val.id || val.value;
        return val;
    };
    
    // FIX: Safely extract the display name
    const extractName = (val: any) => {
        if (val && typeof val === 'object') return val.name || val.text || val.label;
        return undefined;
    };

    return {
        ...draft,
        title: extractId(draft.title) || draft.title,
        titleName: extractName(draft.title) || draft.titleName,

        // FIX: Ensure we keep arrays if they have an ID OR a Name, preventing data loss
        skills: draft.skills?.map((s: any) => ({
            ...s,
            skillId: extractId(s.skillId) || s.id || s._id,
            skillName: extractName(s.skillId) || s.name 
        })).filter((s: any) => s.skillId || s.skillName),

        languages: draft.languages?.map((l: any) => ({
            ...l,
            languageId: extractId(l.languageId) || l.id || l._id,
            languageName: extractName(l.languageId) || l.name
        })).filter((l: any) => l.languageId || l.languageName),

        educations: draft.educations?.map((e: any) => ({
            ...e,
            fieldOfStudyId: extractId(e.fieldOfStudyId) || e.id || e._id,
            degreeTypeId: extractId(e.degreeTypeId),
            fieldOfStudyName: extractName(e.fieldOfStudyId) || e.name
        })).filter((e: any) => e.fieldOfStudyId || e.fieldOfStudyName),

        workExperience: draft.workExperience?.map((w: any) => ({
            ...w,
            jobTitleId: extractId(w.jobTitleId) || w.id || w._id,
            jobTitleName: extractName(w.jobTitleId) || w.name
        })).filter((w: any) => w.jobTitleId || w.jobTitleName),
        
        locationId: extractId(draft.locationId),
        workflowId: extractId(draft.workflowId)
    };
};

function* handleInitiateCreateJob() {
    try {
        // Reset the transaction lock for a new wizard flow
        sessionCreatedJobId = null;
        
        // Ensure the job draft state is completely cleared before starting a new one
        yield put(resetJobDraft());

        let account: any = yield select(selectCurrentAccount);
        if (!account) account = yield select(selectAccountData);

        if (!account) {
             yield put(push('/organization/create/data'));
             return;
        }

        const activeOrg: any = yield select(selectActiveOrganizationDetails);
        const storedOrgId = localStorage.getItem('currentOrganizationId');
        const companies = account.companies || [];
        const isMultiOrg = !!account.isMultiOrg || companies.length > 1;

        let targetOrgId = null;

        if (activeOrg && (activeOrg.id || activeOrg._id)) {
            targetOrgId = activeOrg.id || activeOrg._id;
        } 
        else if (storedOrgId && isMultiOrg) {
            const valid = companies.find((c: any) => (c.id === storedOrgId || c._id === storedOrgId));
            if (valid) targetOrgId = storedOrgId;
        }
        else if (!isMultiOrg && companies.length > 0) {
            const singleOrg = companies[0];
            targetOrgId = singleOrg.id || singleOrg._id;
        } 
        
        if (targetOrgId) {
            try {
                // ENTRANCE GATE: Check Limit Before Entering Wizard
                yield call(api.checkJobLimitApi, targetOrgId);
                
                // --- NEW FIX: Sync the Global Header Context ---
                const targetOrgFull = companies.find((c: any) => c.id === targetOrgId || c._id === targetOrgId);
                if (targetOrgFull) {
                    // FIX: Use setActiveOrganization instead of selectOrganization
                    yield put(setActiveOrganization(targetOrgId));
                    yield put(fetchOrganization(targetOrgId));
                }
                
                // If API succeeds, they are under the limit. Proceed to wizard.
                localStorage.setItem('job_wizard_context_org', targetOrgId);
                yield put(setJobCreationContext(targetOrgId));
                yield put(push('/jobs/new/basics'));
            } catch (limitError: any) {
                // Check if the error is explicitly a limit error
                const errorMessage = limitError.response?.data?.error?.message || limitError.message || '';
                
                if (errorMessage.toLowerCase().includes('limit')) {
                    console.warn('[JobSaga] Entrance Gate Blocked: Limit Reached.');
                    const planUrl = `/settings/plans?context=limit_reached&returnUrl=${encodeURIComponent('/dashboard/recruitment')}`;
                    yield put(push(planUrl));
                } else {
                    // If it failed for another reason (network, org paused), log it and let them proceed (the backend will catch it on save) or show a toast
                    console.warn('[JobSaga] Limit check failed but not due to limit.', errorMessage);
                    toastr.error('Error', errorMessage || 'Failed to verify organization status.');
                }
            }

        } else {
            yield put(openOrgSelector());
        }
    } catch (e) {
        console.error("Failed to initiate job creation", e);
        toastr.error('Error', 'Unable to initialize job creation.');
    }
}

function* handleSubmitJob(action: PayloadAction<{ nextPath?: string; nextStep?: string; isPublishingFree?: boolean } | undefined>) {
    try {
        const rawDraft = yield select(selectJobDraft);
        const payload = flattenDraftForApi(rawDraft);

        // TRANSACTION LOCK: Force injection of the session ID if Redux is lagging behind
        let jobId = payload.id || payload._id || sessionCreatedJobId;
        if (jobId) {
            payload.id = jobId;
        }

        if (!payload.companyId) {
            throw new Error("No organization selected. Please restart the job creation process.");
        }

        let result;

        try {
            // If Free Plan user publishing
            if (action.payload?.isPublishingFree) {
                payload.status = 0;
                if (jobId) {
                    result = yield call(api.updateJob, jobId, payload);
                } else {
                    result = yield call(api.createJob, payload);
                }
                yield put(submitJobSuccess(result));
                const targetId = result.id || result._id || jobId;
                const planUrl = `/settings/plans?context=job_publish&returnUrl=${encodeURIComponent('/dashboard/recruitment')}&entityId=${targetId}&entityType=job`;
                yield put(push(planUrl));
                return;
            }
            
            // Standard Save / Publish
            if (jobId) {
                result = yield call(api.updateJob, jobId, payload);
                toastr.success(i18n.t('job:common.save'), i18n.t('job:new-job.toolbar.title'));
            } else {
                result = yield call(api.createJob, payload);
                const newId = result.id || result._id;
                if (newId) sessionCreatedJobId = newId;

                const skillCount = result.skills?.length || 0;
                const expCount = result.workExperience?.length || 0;
                if (skillCount > 0 || expCount > 0) {
                    toastr.success(
                        i18n.t('job:job-ai-prefilled-title', 'AI Pre-Filled'),
                        i18n.t('job:job-ai-prefilled-message', {
                            skills: skillCount,
                            experience: expCount,
                            defaultValue: `✓ Structured ${skillCount} skills and ${expCount} experience requirements.`
                        })
                    );
                } else {
                    toastr.success(i18n.t('job:common.save'), i18n.t('job:new-job.toolbar.title'));
                }
            }

            yield put(submitJobSuccess(result));

            if (payload.status === 1) {
              const jobCategory = (typeof payload.title === 'string') 
                  ? payload.title 
                  : (payload.title?.text || payload.titleName || 'Unknown Category');

              trackNoviopusEvent('business_job_published', {
                job_category: jobCategory
              });
            }

            const { nextPath, nextStep } = action.payload || {};

            if (nextPath) {
                yield put(push(nextPath));
            } else if (nextStep && result) {
                const newId = result.id || result._id;
                if (newId) {
                    const target = getJobStepPath(newId, nextStep);
                    yield put(push(target));
                }
            }

        } catch (apiError: any) {
            const errorMessage = apiError.response?.data?.error?.message || apiError.message || '';
            if (errorMessage.toLowerCase().includes('limit')) {
                payload.status = 0;
                if (jobId) {
                    result = yield call(api.updateJob, jobId, payload);
                } else {
                    result = yield call(api.createJob, payload);
                }
                yield put(submitJobSuccess(result));
                const targetId = result.id || result._id || jobId;
                const planUrl = `/settings/plans?context=limit_reached&returnUrl=${encodeURIComponent('/dashboard/recruitment')}&entityId=${targetId}&entityType=job`;
                yield put(push(planUrl));
                return;
            }
            throw apiError;
        }

    } catch (error: any) {
        console.error(error);
        const message = error.response?.data?.error?.message || error.message || "Failed to save job";
        yield put(submitJobFailure(message));
        toastr.error('Error', message);
    }
}

function* handleDeleteJobDraft(action: PayloadAction<string>) {
    try {
        yield call(api.deleteJob, action.payload);
        sessionCreatedJobId = null; // Clear lock on deletion
        yield put(deleteJobDraftSuccess());
    } catch (error) {
        console.error("Failed to delete draft", error);
    }
}

function* handleFetchJob(action: ReturnType<typeof fetchJobDetails>) {
    try {
        const jobId = action.payload;
        const rawJob = yield call(api.fetchJob, jobId);
        const normalizedId = rawJob.id || rawJob._id;

        // Lock the session to the fetched job so edits don't spawn duplicates
        sessionCreatedJobId = normalizedId;

        // ... (Draft hydration logic unchanged for brevity, keeping same logic as previous) ...
        const draft: JobDraft = {
             id: normalizedId,
             _id: normalizedId,
             companyId: rawJob.companyId,
             title: (rawJob.title && typeof rawJob.title === 'object' && rawJob.title.id) 
                ? { value: rawJob.title.id, text: rawJob.title.name } 
                : typeof rawJob.title === 'string' ? rawJob.title : (rawJob.title?.name || rawJob.titleName || ''),  
             type: rawJob.type,
             numRecruits: rawJob.numRecruits,
             description: rawJob.description,
             salaryPeriod: rawJob.salary?.period || 'monthly',
             salaryFrom: rawJob.salary?.from || '',
             salaryTo: rawJob.salary?.to || '',
             currency: rawJob.salary?.currency || 'USD',
             useOrganizationDefaults: rawJob.useCompanyBenefits,
             locationId: (Array.isArray(rawJob.locations) && rawJob.locations[0]?.locationId) || (typeof rawJob.location === 'string' ? rawJob.location : rawJob.location?.id) || '',
             isWorkFromHome: rawJob.isWorkFromHome,
             distance: rawJob.distance,
             skills: (rawJob.skills || []).map((s: any) => ({
                 id: s.id || s._id,
                 skillId: s.skillName ? { value: s.skillId, text: s.skillName } : s.skillId,
                 name: s.skillName, 
                 level: s.level,
                 required: s.required
             })),
             languages: (rawJob.languages || []).map((l: any) => ({
                 id: l.id || l._id,
                 languageId: l.languageName ? { value: l.languageId, text: l.languageName } : l.languageId,
                 name: l.languageName,
                 level: l.level,
                 required: l.required
             })),
             educations: (rawJob.educations || []).map((e: any) => ({
                 id: e.id || e._id,
                 degreeTypeId: e.degreeTypeName ? { value: e.degreeTypeId, text: e.degreeTypeName } : e.degreeTypeId,
                 fieldOfStudyId: e.fieldOfStudyName ? { value: e.fieldOfStudyId, text: e.fieldOfStudyName } : e.fieldOfStudyId,
                 required: e.required
             })),
             workExperience: (rawJob.workExperience || []).map((w: any) => ({
                 id: w.id || w._id,
                 jobTitleId: w.jobTitleName ? { value: w.jobTitleId, text: w.jobTitleName } : w.jobTitleId,
                 name: w.jobTitleName,
                 level: w.level,
                 required: w.required
             })),
             workflowId: (rawJob.workflows && rawJob.workflows[0] ? rawJob.workflows[0].id : '') || rawJob.workflow || '',
             workflowName: rawJob.workflowName || 'Standard Workflow',
             hiringManager: rawJob.hiringManager || null,
             teamMembers: rawJob.teamMembers || [],
             stages: rawJob.stages || DEFAULT_STAGES
        };
        
        // FIX: REMOVED the block of code that was forcefully destroying the object
        // if (typeof draft.title === 'object' && draft.title !== null) {
        //     draft.title = (draft.title as any).name || '';
        // }
        
        yield put(loadJobForEdit(draft));
    } catch (error: any) {
        console.error("Fetch Job Error:", error);
        toastr.error('Error', 'Failed to load job details');
    }
}

function* handlePreFlightJDParsing(action: ReturnType<typeof startPreFlightJDParsing>) {
    try {
        const { description } = action.payload;
        if (!description || description.trim().length < 30) {
            return;
        }
        yield put(startParsing());
        const result = yield call(api.parseJobDescriptionApi, description);
        yield put(parsingSuccess(result));
    } catch (e) {
        console.warn('[PreFlight ATS] Background JD parse failed:', e);
        yield put(parsingFailure());
    }
}

function* handleParsing() {
    try {
        const draft = yield select(selectJobDraft);
        if (!draft.description) {
            yield put(parsingFailure());
            return;
        }
        const result = yield call(api.parseJobDescriptionApi, draft.description);
        yield put(parsingSuccess(result));
    } catch (e) {
        yield put(parsingFailure());
    }
}

export function* jobCreateSaga() {
    yield takeLeading(submitJob.type, handleSubmitJob);
    yield takeLatest(deleteJobDraft.type, handleDeleteJobDraft);
    yield takeLatest(startParsing.type, handleParsing);
    yield takeLatest(startPreFlightJDParsing.type, handlePreFlightJDParsing);
    yield takeLatest(fetchJobDetails.type, handleFetchJob);
    yield takeLatest(initiateCreateJob.type, handleInitiateCreateJob);
}



